import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lightbulb,
  ArrowRight,
  Loader2,
  RotateCcw,
  PocketKnife,
  Zap,
  History,
  Trash2,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { SolutionCard } from "@/components/SolutionCard";
import { MicButton } from "@/components/MicButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HISTORY_KEY = "yic_history";

const PRESETS = [
  "Pneu crevé et je n'ai pas de cric sous la main",
  "Mes clés sont tombées derrière un meuble très lourd",
  "Réunion vidéo urgente mais mon micro ne marche plus",
  "Bocal impossible à ouvrir, le couvercle est bloqué",
];

const LOADING_MSGS = [
  "Analyse MacGyver en cours...",
  "Détournement d'objets du quotidien...",
  "Recherche de la solution trombone...",
  "Assemblage des plans de secours...",
];

export default function Home() {
  const [problem, setProblem] = useState("");
  const [userSolution, setUserSolution] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [loadMsg, setLoadMsg] = useState(LOADING_MSGS[0]);
  const [history, setHistory] = useState([]);
  const resultsRef = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) setHistory(JSON.parse(raw));
    } catch (e) {
      /* ignore */
    }
  }, []);

  const persist = (items) => {
    setHistory(items);
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(items));
    } catch (e) {
      /* ignore */
    }
  };

  const runLoadingMessages = () => {
    let i = 0;
    const id = setInterval(() => {
      i = (i + 1) % LOADING_MSGS.length;
      setLoadMsg(LOADING_MSGS[i]);
    }, 1600);
    return id;
  };

  const handleSolve = async () => {
    if (!problem.trim()) {
      toast.error("Décris d'abord ton problème !");
      return;
    }
    setLoading(true);
    setResult(null);
    const msgId = runLoadingMessages();
    try {
      const { data } = await axios.post(`${API}/solve`, {
        problem: problem.trim(),
        user_solution: userSolution.trim(),
      });
      setResult(data);
      const entry = {
        id: Date.now(),
        problem: problem.trim(),
        userSolution: userSolution.trim(),
        result: data,
        ts: new Date().toISOString(),
      };
      persist([entry, ...history].slice(0, 10));
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (e) {
      console.error(e);
      toast.error("Le moteur a eu un souci. Réessaie dans un instant.");
    } finally {
      clearInterval(msgId);
      setLoading(false);
    }
  };

  const openHistory = (entry) => {
    setProblem(entry.problem);
    setUserSolution(entry.userSolution || "");
    setResult(entry.result);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const clearHistory = () => {
    persist([]);
    toast.success("Historique effacé");
  };

  const reset = () => {
    setProblem("");
    setUserSolution("");
    setResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="grain relative min-h-screen bg-[#f4efe4] blueprint-grid">
      <Toaster position="top-center" theme="light" richColors />

      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-[#e4ddce] bg-[#f4efe4]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3.5 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg border border-[#e4ddce] bg-white shadow-sm">
              <img
                src="/logo.png"
                alt="Yes I can logo"
                className="h-9 w-9 object-contain"
                data-testid="app-logo"
              />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-black uppercase leading-none tracking-tight text-slate-900">
                Yes I can
              </h1>
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-red-600">
                MacGyver Resolution Engine
              </span>
            </div>
          </div>
          <span className="hidden rounded-md border border-[#d8d0bf] bg-white/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500 sm:block">
            v3.8 · Toujours une solution
          </span>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-4 pb-24 lg:px-8">
        {/* Hero */}
        <section className="pt-14 lg:pt-20">
          <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-4 py-1.5">
              <span className="pulse-dot h-2 w-2 rounded-full bg-red-500" />
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-red-600">
                Aucun problème n'est vraiment sans issue
              </span>
            </div>
            <h2 className="font-heading text-4xl font-black uppercase leading-[0.95] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Coincé ? Pas de panique.
              <br />
              <span className="text-red-600">On trouve TOUJOURS</span> une solution.
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600">
              Décris ton problème « impossible » (et ton idée si tu en as une), que ce soit
              un problème pro, un problème dans la vie, n'importe quoi. Le moteur
              MacGyver te renvoie{" "}
              <span className="font-semibold text-slate-900">3 solutions réelles et applicables</span> — dont
              au moins une d'une simplicité désarmante. Niveau trombone garanti.
            </p>
          </motion.div>

          {/* MacGyver mascot + speech bubble */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, x: 20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-[260px] shrink-0 lg:mx-0 lg:max-w-[300px]"
            data-testid="macgyver-mascot"
          >
            {/* Speech bubble */}
            <div className="relative z-10 mx-auto mb-2 w-fit max-w-[240px] rounded-2xl border-2 border-slate-900 bg-white px-4 py-3 shadow-[4px_4px_0_0_rgba(220,38,38,0.9)]">
              <p className="font-heading text-lg font-bold uppercase leading-tight tracking-tight text-slate-900 sm:text-xl">
                Tu vas y arriver, fieu&nbsp;!
              </p>
              <span className="absolute -bottom-[10px] left-10 h-0 w-0 border-l-[12px] border-r-0 border-t-[14px] border-l-transparent border-t-slate-900" />
              <span className="absolute -bottom-[6px] left-[42px] h-0 w-0 border-l-[9px] border-r-0 border-t-[10px] border-l-transparent border-t-white" />
            </div>
            <img
              src="/macgyver.png?v=2"
              alt="MacGyver qui t'encourage"
              className="float-y mx-auto w-full rounded-2xl border-2 border-slate-900/10 shadow-xl"
              data-testid="macgyver-image"
            />
          </motion.div>
          </div>
        </section>

        {/* Input dock */}
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-10 rounded-2xl border border-[#e4ddce] bg-white p-5 shadow-[0_18px_50px_-20px_rgba(120,100,60,0.35)] lg:p-8"
        >
          <div className="mb-2 flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-500">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
              Ton problème
            </label>
            <MicButton
              value={problem}
              onChange={setProblem}
              testId="dictate-problem-button"
              accent="#dc2626"
            />
          </div>
          <textarea
            data-testid="problem-input-textarea"
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            placeholder="Ex : Je dois planter un clou mais je n'ai pas de marteau..."
            rows={3}
            className="w-full resize-none rounded-xl border border-[#e0d8c6] bg-[#faf7f0] px-4 py-3.5 text-base text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-red-500/60 focus:bg-white focus:ring-2 focus:ring-red-500/20"
          />

          <div className="mb-2 mt-5 flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-500">
              <Lightbulb size={13} className="text-amber-500" />
              Ton idée (optionnel)
            </label>
            <MicButton
              value={userSolution}
              onChange={setUserSolution}
              testId="dictate-idea-button"
              accent="#f59e0b"
            />
          </div>
          <textarea
            data-testid="user-solution-input-textarea"
            value={userSolution}
            onChange={(e) => setUserSolution(e.target.value)}
            placeholder="Ce que tu pensais faire, même si tu doutes que ça marche..."
            rows={2}
            className="w-full resize-none rounded-xl border border-[#e0d8c6] bg-[#faf7f0] px-4 py-3.5 text-base text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-amber-500/60 focus:bg-white focus:ring-2 focus:ring-amber-500/20"
          />

          {/* Presets */}
          <div className="mt-5">
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
              Galères express à tester :
            </span>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {PRESETS.map((p, i) => (
                <button
                  key={i}
                  data-testid={`quick-preset-button-${i}`}
                  onClick={() => setProblem(p)}
                  className="rounded-full border border-[#e0d8c6] bg-[#faf7f0] px-3.5 py-1.5 text-xs text-slate-600 transition-all hover:border-amber-500/50 hover:bg-amber-50 hover:text-amber-700"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <button
            data-testid="generate-solutions-button"
            onClick={handleSolve}
            disabled={loading}
            className="group mt-7 flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-red-600 to-red-700 py-4 font-heading text-lg font-bold uppercase tracking-wide text-white shadow-lg shadow-red-500/30 transition-all hover:from-red-500 hover:to-red-600 hover:shadow-red-500/50 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? (
              <>
                <Loader2 size={22} className="animate-spin" />
                {loadMsg}
              </>
            ) : (
              <>
                <Zap size={22} />
                Trouver les solutions maintenant
                <ArrowRight size={22} className="transition-transform group-hover:translate-x-1" />
              </>
            )}
          </button>
        </motion.section>

        {/* Results */}
        <div ref={resultsRef}>
          <AnimatePresence>
            {result && (
              <motion.section
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="mt-16"
                data-testid="solutions-results"
              >
                <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                  <div>
                    <span className="font-mono text-xs uppercase tracking-[0.22em] text-emerald-600">
                      Mission accomplie
                    </span>
                    <p
                      data-testid="encouragement-text"
                      className="font-heading mt-1 text-2xl font-bold uppercase tracking-tight text-slate-900 sm:text-3xl"
                    >
                      {result.encouragement}
                    </p>
                  </div>
                  <button
                    data-testid="reset-button"
                    onClick={reset}
                    className="flex shrink-0 items-center gap-2 rounded-lg border border-[#e0d8c6] bg-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-slate-600 transition-colors hover:border-slate-400 hover:bg-[#faf7f0]"
                  >
                    <RotateCcw size={14} />
                    Nouveau problème
                  </button>
                </div>

                <div className="grid gap-6 lg:grid-cols-3">
                  <SolutionCard type="super_simple" data={result.super_simple} index={0} />
                  <SolutionCard type="macgyver_special" data={result.macgyver_special} index={1} />
                  <SolutionCard type="tactical_pro" data={result.tactical_pro} index={2} />
                </div>

                {result.user_idea && (
                  <motion.div
                    data-testid="user-idea-comparison-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mt-6 rounded-xl border border-[#e4ddce] bg-white p-6 shadow-sm lg:p-7"
                  >
                    <div className="mb-3 flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-300 bg-slate-100">
                        <Lightbulb size={22} className="text-slate-500" />
                      </div>
                      <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                        Ton idée initiale, revisitée
                      </span>
                    </div>
                    <p className="font-heading mb-3 text-xl font-bold uppercase tracking-tight text-slate-900">
                      {result.user_idea.verdict}
                    </p>
                    <p className="mb-4 text-sm leading-relaxed text-slate-600">
                      {result.user_idea.analysis}
                    </p>
                    <div className="rounded-lg border border-amber-500/25 bg-amber-50 p-4">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-amber-600">
                        Le boost MacGyver
                      </span>
                      <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                        {result.user_idea.upgrade}
                      </p>
                    </div>
                  </motion.div>
                )}
              </motion.section>
            )}
          </AnimatePresence>
        </div>

        {!result && !loading && history.length > 0 && (
          <section className="mx-auto mt-20 max-w-4xl" data-testid="history-section">
            <div className="mb-5 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <History size={18} className="text-red-600" />
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">
                  Tes 10 dernières demandes
                </h2>
              </div>
              <button
                data-testid="clear-history-button"
                onClick={clearHistory}
                className="flex items-center gap-1.5 rounded-lg border border-[#e0d8c6] bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-slate-500 transition-colors hover:border-red-400 hover:text-red-600"
              >
                <Trash2 size={13} />
                Vider
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {history.map((entry) => (
                <button
                  key={entry.id}
                  data-testid={`history-item-${entry.id}`}
                  onClick={() => openHistory(entry)}
                  className="group flex items-start gap-3 rounded-xl border border-[#e4ddce] bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-red-300 hover:shadow-md"
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
                    <PocketKnife size={16} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-slate-800">
                      {entry.problem}
                    </span>
                    <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-widest text-slate-400">
                      {new Date(entry.ts).toLocaleString("fr-FR", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </span>
                  <ArrowRight
                    size={16}
                    className="mt-0.5 shrink-0 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-red-500"
                  />
                </button>
              ))}
            </div>
          </section>
        )}
      </main>

      <footer className="relative z-10 border-t border-[#e4ddce] py-8 text-center">
        <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">
          Yes I can · Il y a toujours une solution — il suffit de la trouver
        </p>
      </footer>
    </div>
  );
}
